import React from "react";
import Topbar, { TopbarProps } from "../../widgets/Topbar/Topbar";
import ssrComponent from "../../../utils/ssr";
import { flowRight } from "lodash";
import { wrapWithThemeProvider } from "../../../utils/themeProvider";
import { wrapWithApollo } from "../../../graphql/client";
import { withPersistSSR } from "../../../utils/PersistSSR";
import isSSR from "../../../utils/isSSR";
import { wrapWithRorpContext } from "../../../contexts/RorpContext";
import wrapWithStylesProvider from "../../../utils/wrapWithStylesProvider";
import { wrapWithRouter } from "../../../utils/Router";
import { wrapWithRoutes } from "../../../utils/routes";
import { initializeLocale } from "../../../utils/locale";
import Footer from "../../widgets/Footer/Footer";
import ProductFeedbackSurvey from "../../widgets/TypeformSurveys/ProductFeedbackSurvey";
import { SearchPageProps } from "../SearchPage/SearchPage";
import { wrapWithAlgoliaSSR } from "../../../utils/algolia/ssr";
import SearchProvider from "../../../utils/algolia/SearchProvider";

interface Props {
  topbarProps: TopbarProps;
  componentProps?: SearchPageProps;
  withSearch: boolean;
  children: React.ReactNode;
}

function WithSearch({ withSearch, componentProps, children }: Props) {
  return withSearch ? (
    <SearchProvider searchPageProps={componentProps}>{children}</SearchProvider>
  ) : (
    <>{children}</>
  );
}

function FullPageLayout({ children, ...props }: Props) {
  const { topbarProps } = props;
  initializeLocale(topbarProps.i18n);

  // Hiding footer and topbar based on display prop
  return (
    <WithSearch {...props}>
      {!topbarProps.isAirrobeNativeView && <Topbar {...topbarProps} />}
      <ProductFeedbackSurvey />
      {children}
      {!topbarProps.isAirrobeNativeView && <Footer />}
    </WithSearch>
  );
}

export default FullPageLayout;

const wrappedFullPageLayout = (context, pageProps, componentProps, Component) =>
  flowRight(
    wrapWithAlgoliaSSR,
    wrapWithRoutes(context),
    wrapWithRorpContext(context),
    wrapWithRouter(context),
    wrapWithStylesProvider,
    wrapWithThemeProvider,
    wrapWithApollo,
    withPersistSSR
  )(() => {
    return (
      <FullPageLayout {...pageProps}>
        <Component {...componentProps} />
      </FullPageLayout>
    );
  });

export type EntryProps = {
  pageProps: object;
  componentProps?: object;
  algolia?: boolean;
};

export type MapClientEntryProps<Args> = (args: Args, context) => EntryProps;
export type MapServerEntryProps<Args> = (args: Args, context) => EntryProps | Promise<EntryProps>;

export const createEntryPage = (
  mapClientEntryProps: MapClientEntryProps<any>,
  mapServerEntryProps: MapServerEntryProps<any> = mapClientEntryProps
) => (Component) => {
  if (isSSR())
    return async (args, context) => {
      const { pageProps, componentProps, algolia } = await mapServerEntryProps(args, context);
      return await ssrComponent(
        wrappedFullPageLayout(context, pageProps, componentProps, Component),
        { algolia }
      );
    };

  return (args, context) => {
    const { pageProps, componentProps } = mapClientEntryProps(args, context);
    return wrappedFullPageLayout(context, pageProps, componentProps, Component);
  };
};
